import { isFuzzyMatch, substringMatchScore } from '@blocksuite/affine-shared/utils';

import type {
  SlashMenuActionContext,
  SlashMenuActionItem,
  SlashMenuConfig,
  SlashMenuContext,
  SlashMenuItem,
  SlashMenuSubMenu,
} from './types';

export function isActionItem(item: SlashMenuItem): item is SlashMenuActionItem {
  return 'action' in item;
}

export function isSubMenuItem(item: SlashMenuItem): item is SlashMenuSubMenu {
  return 'subMenu' in item;
}

export function slashItemClassName({ name }: SlashMenuItem) {
  return name.split(' ').join('-').toLocaleLowerCase();
}

function normalizeCommandText(text: string) {
  return text.trim().replace(/\s+/g, ' ').toLocaleLowerCase();
}

function getCommandCandidates(item: SlashMenuItem) {
  return [item.name, ...(item.searchAlias ?? [])]
    .map(command => ({
      command,
      normalized: normalizeCommandText(command),
    }))
    .filter(({ normalized }) => normalized.length > 0)
    .sort((a, b) => b.normalized.length - a.normalized.length);
}

export function getSlashMenuActionContext(
  item: SlashMenuActionItem,
  query: string
): SlashMenuActionContext | undefined {
  if (!item.acceptsArgument) return;

  const trimmedQuery = query.trimStart().replace(/^\//, '');
  const normalizedQuery = normalizeCommandText(trimmedQuery);
  if (!normalizedQuery) return;

  const matched = getCommandCandidates(item).find(({ normalized }) => {
    return (
      normalizedQuery === normalized ||
      normalizedQuery.startsWith(`${normalized} `)
    );
  });

  if (!matched) return;

  return {
    query,
    command: matched.command,
    argument: trimmedQuery.slice(matched.command.length).trim(),
  };
}

export function acceptsSlashMenuArgument(item: SlashMenuItem, query: string) {
  return isActionItem(item) && !!getSlashMenuActionContext(item, query);
}

export type SlashMenuQueryState = 'off' | 'on' | 'no_result';

export type SlashMenuFilterResult = {
  items: SlashMenuItem[];
  state: SlashMenuQueryState;
};

/**
 * Resolves the query typed after the trigger key into the list of items to
 * show. Shared by the desktop popover and the mobile menu so both agree on
 * argument mode, fuzzy matching and the empty/no-result states.
 */
export function filterSlashMenuItems(
  items: SlashMenuItem[],
  query: string,
  context: SlashMenuContext
): SlashMenuFilterResult {
  const searchStr = query.toLowerCase();

  // Argument mode: an item that accepts a trailing argument takes over the
  // whole menu, optionally expanding into its own dynamic suggestions.
  const argumentItems = items.flatMap(item => {
    if (!acceptsSlashMenuArgument(item, query)) return [];
    if (isActionItem(item) && item.getArgumentItems) {
      const actionContext = getSlashMenuActionContext(item, query);
      if (!actionContext) return [item];
      const expanded = item.getArgumentItems(context, actionContext);
      return expanded.length > 0 ? expanded : [item];
    }
    return [item];
  });
  if (argumentItems.length > 0) {
    return { items: argumentItems, state: 'on' };
  }

  if (searchStr === '' || searchStr.endsWith(' ')) {
    return { items: [], state: searchStr === '' ? 'off' : 'no_result' };
  }

  // Layer order traversal, searching the first two levels only.
  let filtered: SlashMenuItem[] = [];
  let depth = 0;
  let queue = items;
  while (queue.length !== 0) {
    // remove the sub menu item from the previous layer result
    filtered = filtered.filter(item => !isSubMenuItem(item));

    filtered = filtered.concat(
      queue.filter(({ name, searchAlias = [] }) =>
        [name, ...searchAlias].some(str => isFuzzyMatch(str, searchStr))
      )
    );

    if (filtered.length !== 0 && depth >= 1) break;

    queue = queue.flatMap(item => (isSubMenuItem(item) ? item.subMenu : []));

    depth++;
  }

  filtered.sort(
    (a, b) =>
      -(substringMatchScore(a.name, searchStr) -
        substringMatchScore(b.name, searchStr))
  );

  return { items: filtered, state: filtered.length === 0 ? 'no_result' : 'on' };
}

export function parseGroup(group: NonNullable<SlashMenuItem['group']>) {
  return [
    parseInt(group.split('_')[0]),
    group.split('_')[1].split('@')[0],
    parseInt(group.split('@')[1]),
  ] as const;
}

function itemCompareFn(a: SlashMenuItem, b: SlashMenuItem) {
  if (a.group === undefined && b.group === undefined) return 0;
  if (a.group === undefined) return -1;
  if (b.group === undefined) return 1;

  const [aGroupIndex, aGroupName, aItemIndex] = parseGroup(a.group);
  const [bGroupIndex, bGroupName, bItemIndex] = parseGroup(b.group);
  if (isNaN(aGroupIndex)) return -1;
  if (isNaN(bGroupIndex)) return 1;
  if (aGroupIndex < bGroupIndex) return -1;
  if (aGroupIndex > bGroupIndex) return 1;

  if (aGroupName !== bGroupName) return aGroupName.localeCompare(bGroupName);

  if (isNaN(aItemIndex)) return -1;
  if (isNaN(bItemIndex)) return 1;

  return aItemIndex - bItemIndex;
}

export function buildSlashMenuItems(
  items: SlashMenuItem[],
  context: SlashMenuContext,
  transform?: (item: SlashMenuItem) => SlashMenuItem
): SlashMenuItem[] {
  if (transform) items = items.map(transform);

  const result = items
    .filter(item => (item.when ? item.when(context) : true))
    .sort(itemCompareFn)
    .map(item => {
      if (isSubMenuItem(item)) {
        return {
          ...item,
          subMenu: buildSlashMenuItems(item.subMenu, context),
        };
      } else {
        return { ...item };
      }
    });
  return result;
}

export function mergeSlashMenuConfigs(
  configs: Map<string, SlashMenuConfig>
): SlashMenuConfig {
  return {
    items: ctx =>
      Array.from(configs.values()).flatMap(({ items }) =>
        typeof items === 'function' ? items(ctx) : items
      ),
    disableWhen: ctx =>
      configs
        .values()
        .map(({ disableWhen }) => disableWhen?.(ctx) ?? false)
        .some(Boolean),
  };
}

export function formatDate(date: Date) {
  // yyyy-mm-dd
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const strTime = `${year}-${month}-${day}`;
  return strTime;
}

export function formatTime(date: Date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const strTime = `${formatDate(date)} ${hours}:${minutes}`;
  return strTime;
}

export function formatTimeOfDay(date: Date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
}

export function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

export function addMinutes(date: Date, minutes: number) {
  const result = new Date(date);
  result.setMinutes(result.getMinutes() + minutes);
  return result;
}

export function addHours(date: Date, hours: number) {
  const result = new Date(date);
  result.setHours(result.getHours() + hours);
  return result;
}

export function addMonthsClamped(date: Date, months: number) {
  const result = new Date(date);
  const day = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const lastDayOfMonth = new Date(
    result.getFullYear(),
    result.getMonth() + 1,
    0
  ).getDate();
  result.setDate(Math.min(day, lastDayOfMonth));
  return result;
}

export function getNextWeekday(date: Date, weekday: number) {
  const diff = (weekday - date.getDay() + 7) % 7 || 7;
  return addDays(date, diff);
}
